import { z } from 'zod';
import { TimestampSchema } from '../common/clock.js';
import { AgentSessionIdSchema, ApplicationIdSchema, CustomerIdSchema, DocumentIdSchema, HumanTaskIdSchema, MemoryCheckpointIdSchema, MemoryFactIdSchema, } from '../common/ids.js';
import { paginatedSchema } from '../common/pagination.js';
import { ReasonCodeSchema } from '../reason-codes.js';
import { AgentCardSchema, CardActionSchema } from './cards.js';
import { AgentChannelSchema, AgentRoleSchema, AgentSessionStatusSchema, GuardNameSchema, GuardOutcomeSchema, GuardStageSchema, HumanTaskPrioritySchema, HumanTaskStatusSchema, HumanTaskTypeSchema, MemoryFactStatusSchema, MemorySourceTypeSchema, PublicAgentSchema, ToolInvocationStatusSchema, TurnRoleSchema, } from './states.js';
// ── Sesiones y mensajes ───────────────────────────────────────────────────
export const CreateAgentSessionRequestSchema = z
    .object({
    agent: PublicAgentSchema,
    applicationId: ApplicationIdSchema.optional(),
    channel: AgentChannelSchema.exclude(['CONSOLE']),
})
    .meta({ id: 'CreateAgentSessionRequest' });
export const CreateAgentSessionResponseSchema = z
    .object({
    id: AgentSessionIdSchema,
    greeting: z.string(),
    cards: z.array(AgentCardSchema).default([]),
})
    .meta({ id: 'CreateAgentSessionResponse' });
export const AgentMessageSchema = z
    .object({
    seq: z.int(),
    role: TurnRoleSchema,
    text: z.string().nullable(),
    cards: z.array(AgentCardSchema),
    attachments: z.array(DocumentIdSchema),
    inputMode: z.enum(['TEXT', 'VOICE', 'FILE']).nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'AgentMessage' });
export const AgentMessagePageSchema = paginatedSchema(AgentMessageSchema);
export const AgentSessionSchema = z
    .object({
    id: AgentSessionIdSchema,
    agent: PublicAgentSchema,
    status: AgentSessionStatusSchema,
    applicationId: ApplicationIdSchema.nullable(),
    channel: AgentChannelSchema,
    lastMessages: z.array(AgentMessageSchema),
    startedAt: TimestampSchema,
    lastActivityAt: TimestampSchema,
})
    .meta({ id: 'AgentSession' });
/** `POST /v1/agent/sessions/{id}/messages` (idempotente por `clientMessageId`) → SSE. */
export const PostAgentMessageRequestSchema = z
    .object({
    clientMessageId: z.uuid(),
    text: z.string().trim().max(4000).optional(),
    attachments: z.array(DocumentIdSchema).max(5).default([]),
    inputMode: z.enum(['TEXT', 'VOICE', 'FILE']).default('TEXT'),
    /** Si el texto viene de `/v1/agent/voice/transcribe`: se guarda en el turno (canal voz). */
    voice: z
        .object({ confidence: z.number().min(0).max(1), durationMs: z.int().min(0) })
        .optional(),
})
    .refine((v) => (v.text?.length ?? 0) > 0 || v.attachments.length > 0, {
    message: 'Envía texto o adjuntos',
})
    .meta({ id: 'PostAgentMessageRequest' });
/** Eventos SSE del streaming del agente. */
export const AgentSseEventSchema = z.discriminatedUnion('event', [
    z.object({ event: z.literal('message.delta'), data: z.object({ text: z.string() }) }),
    z.object({
        event: z.literal('tool.started'),
        data: z.object({ tool: z.string(), label: z.string() }),
    }),
    z.object({
        event: z.literal('tool.completed'),
        data: z.object({ tool: z.string(), status: ToolInvocationStatusSchema }),
    }),
    z.object({ event: z.literal('card'), data: AgentCardSchema }),
    /** Cambio de estado de la sesión (p. ej. `WAITING_HITL`, `TAKEN_OVER`) o aviso de presupuesto. */
    z.object({
        event: z.literal('session.status'),
        data: z.object({
            status: AgentSessionStatusSchema,
            notice: z
                .enum(['BUDGET_WARNING', 'BUDGET_EXHAUSTED', 'HUMAN_JOINED', 'AGENT_RESUMED'])
                .optional(),
        }),
    }),
    z.object({
        event: z.literal('message.completed'),
        data: z.object({ seq: z.int(), text: z.string() }),
    }),
    z.object({
        event: z.literal('error'),
        data: z.object({ reasonCode: ReasonCodeSchema, message: z.string() }),
    }),
]);
export const CardActionRequestSchema = z
    .object({ action: CardActionSchema, payload: z.record(z.string(), z.unknown()).default({}) })
    .meta({ id: 'CardActionRequest' });
export const CardActionResponseSchema = z
    .object({
    ok: z.boolean(),
    scaChallengeId: z.string().optional(),
    card: AgentCardSchema.optional(),
    message: z.string().optional(),
})
    .meta({ id: 'CardActionResponse' });
// ── Voz ───────────────────────────────────────────────────────────────────
export const TranscribeResponseSchema = z.object({
    text: z.string(),
    confidence: z.number().min(0).max(1),
});
export const SynthesizeRequestSchema = z.object({
    text: z.string().min(1).max(3000),
    voice: z.string().optional(),
});
export const MAX_VOICE_SECONDS = 60;
/** `GET /v1/agent/voice/config`: sin voz del servidor, `fallback: "browser"` (Web Speech API). */
export const VoiceConfigSchema = z
    .object({
    enabled: z.boolean(),
    voice: z.string().optional(),
    language: z.string().default('es-CO'),
    maxSeconds: z.int().default(MAX_VOICE_SECONDS),
    fallback: z.enum(['browser']).nullable().default(null),
})
    .meta({ id: 'VoiceConfig' });
const t = (name, agents, effect, requiresConfirmation, label) => ({ name, agents, effect, requiresConfirmation, label });
const ADV = 'ORIGINATION_ADVISOR';
const PUR = 'PURCHASE_ASSISTANT';
const COP = 'ANALYST_COPILOT';
/** Garantía: ninguna herramienta confirma pagos, aprueba créditos, abre líneas, cambia cupos ni reversa compras. */
export const AGENT_TOOLS = [
    t('search_knowledge', [ADV, PUR, COP], 'read', 'NO', 'Consultando la información del banco…'),
    t('get_application_state', [ADV, COP], 'read', 'NO', 'Revisando tu solicitud…'),
    t('advance_phase', [ADV], 'write-internal', 'NO', 'Avanzando al siguiente paso…'),
    t('check_preeligibility', [ADV], 'read', 'NO', 'Revisando si cumples los requisitos básicos…'),
    t('record_consent', [ADV], 'write-internal', 'OTP', 'Registrando tu autorización…'),
    t('request_document', [ADV], 'write-internal', 'NO', 'Preparando la carga del documento…'),
    t('review_document', [ADV], 'write-internal', 'NO', 'Revisando tu documento (menos de un minuto)…'),
    t('verify_identity', [ADV], 'write-internal', 'NO', 'Verificando tu identidad…'),
    t('propose_memory_facts', [ADV], 'write-internal', 'YES', 'Tomando nota…'),
    t('confirm_memory_facts', [ADV], 'write-internal', 'YES', 'Confirmando los datos…'),
    t('financial_calculator', [ADV], 'read', 'NO', 'Haciendo cuentas…'),
    t('build_cash_flow', [ADV], 'write-internal', 'YES', 'Armando tu flujo de caja…'),
    t('simulate_credit', [ADV, PUR], 'read', 'NO', 'Simulando las cuotas…'),
    t('get_offer', [ADV], 'read', 'NO', 'Consultando tu oferta…'),
    t('submit_application', [ADV], 'write-internal', 'YES', 'Enviando tu solicitud…'),
    t('accept_terms_draft', [ADV], 'draft', 'OTP', 'Preparando la firma…'),
    t('schedule_followup', [ADV], 'write-internal', 'NO', 'Agendando un recordatorio…'),
    // El asesor de originación acompaña todo el proceso: no pasa la conversación a un humano.
    t('request_human', [PUR, COP], 'write-internal', 'NO', 'Llamando a un asesor…'),
    t('parse_breb_qr', [PUR], 'read', 'NO', 'Leyendo el QR…'),
    t('resolve_payee', [PUR], 'read', 'NO', 'Buscando al comercio…'),
    t('save_payee_nickname', [PUR], 'write-internal', 'YES', 'Guardando el nombre del comercio…'),
    t('get_credit_summary', [PUR, ADV], 'read', 'NO', 'Consultando tu cupo…'),
    t('get_upcoming_installments', [PUR], 'read', 'NO', 'Revisando tus próximas cuotas…'),
    t('get_statement', [PUR], 'read', 'NO', 'Consultando tu extracto…'),
    t('list_purchases', [PUR], 'read', 'NO', 'Revisando tus compras…'),
    t('create_payment_draft', [PUR], 'draft', 'CARD_AND_SCA', 'Preparando el pago…'),
    t('create_installment_payment_draft', [PUR], 'draft', 'CARD_AND_SCA', 'Preparando el pago de tu cuota…'),
    t('create_dispute_draft', [PUR], 'draft', 'YES', 'Preparando tu reclamo…'),
    t('summarize_application', [COP], 'read', 'NO', 'Resumiendo el expediente…'),
    t('compare_with_policy', [COP], 'read', 'NO', 'Comparando con la política…'),
    t('draft_decision_rationale', [COP], 'draft', 'YES', 'Redactando la justificación…'),
];
// ── Trazas, guardas y HITL ────────────────────────────────────────────────
export const AgentToolInvocationSchema = z.object({
    toolName: z.string(),
    status: ToolInvocationStatusSchema,
    input: z.unknown(),
    output: z.unknown(),
    durationMs: z.int(),
    actingCustomerId: CustomerIdSchema.nullable(),
    actingScopes: z.array(z.string()),
});
export const GuardEventSchema = z.object({
    stage: GuardStageSchema,
    guard: GuardNameSchema,
    outcome: GuardOutcomeSchema,
    details: z.record(z.string(), z.unknown()),
    at: TimestampSchema,
});
export const HumanTaskSchema = z
    .object({
    id: HumanTaskIdSchema,
    type: HumanTaskTypeSchema,
    subjectType: z.string(),
    subjectId: z.string(),
    priority: HumanTaskPrioritySchema,
    status: HumanTaskStatusSchema,
    assignedTo: z.string().nullable(),
    dueAt: TimestampSchema.nullable(),
    overdue: z.boolean(),
    resolution: z.record(z.string(), z.unknown()).nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'HumanTask' });
export const MemoryFactSchema = z
    .object({
    id: MemoryFactIdSchema,
    key: z.string().regex(/^[a-z_]+(\.[a-z_]+)+$/),
    value: z.unknown(),
    sourceType: MemorySourceTypeSchema,
    sourceRef: z.string().nullable(),
    confidence: z.string(),
    status: MemoryFactStatusSchema,
    validFrom: TimestampSchema,
    validTo: TimestampSchema.nullable(),
})
    .meta({ id: 'MemoryFact' });
/** `GET /v1/customers/me/memory`: hechos vigentes del cliente con etiqueta para la UI. */
export const CustomerMemoryFactSchema = MemoryFactSchema.extend({
    label: z.string(),
    /** Requiere confirmación explícita del cliente (ventas, gastos, ingresos, deudas). */
    critical: z.boolean(),
}).meta({ id: 'CustomerMemoryFact' });
export const CustomerMemoryResponseSchema = z.object({ data: z.array(CustomerMemoryFactSchema) });
export const MemoryRollbackRequestSchema = z.object({ checkpointId: MemoryCheckpointIdSchema });
export const KillSwitchRequestSchema = z.object({ agent: AgentRoleSchema, enabled: z.boolean() });
//# sourceMappingURL=schemas.js.map